import { useEffect, useId, useState } from "react";
import { AlertCircle, Loader2, Phone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useClientTab } from "@/hooks/useClients";
import { useQuickLogCall } from "@/hooks/useClient360Tasks";
import { OutcomePicker, noteRequired } from "./OutcomePicker";
import { PaymentPromiseFields } from "./PaymentPromiseFields";
import type {
  Client360QuickLogPayload,
  PaymentPromiseInput,
  TaskOutcome,
} from "@/api/client360Tasks";

interface ContactLite { id: string; name: string; position?: string }

/**
 * 3A — «Зафіксувати дзвінок». Records an unplanned call as a COMPLETED call record
 * (NOT Activity CRUD). outcome=promised_payment reveals the Payment Promise P0-lite
 * sub-form (+ auto follow-up). On success → timeline shows «Дзвінок зафіксовано».
 */
export function QuickCallDrawer({
  open,
  onClose,
  holdingId,
  overdueAmount,
}: {
  open: boolean;
  onClose: () => void;
  holdingId: string;
  overdueAmount?: number | null;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();
  const contactsQ = useClientTab<ContactLite>(holdingId, "contacts", open);
  const mutation = useQuickLogCall(holdingId);

  const [contactId, setContactId] = useState("");
  const [outcome, setOutcome] = useState<TaskOutcome | "">("");
  const [note, setNote] = useState("");
  const [pp, setPp] = useState<PaymentPromiseInput>({ promisedPaymentDate: "", amount: null, createFollowUp: true });
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) return;
    setContactId(""); setOutcome(""); setNote("");
    setPp({ promisedPaymentDate: "", amount: null, createFollowUp: true });
    setErr(null);
  }, [open]);

  if (!open) return null;

  const isPromise = outcome === "promised_payment";
  const needNote = noteRequired(outcome);
  const canSubmit =
    outcome !== "" &&
    (!needNote || note.trim().length > 0) &&
    (!isPromise || /^\d{4}-\d{2}-\d{2}$/.test(pp.promisedPaymentDate)) &&
    !mutation.isPending;

  const submit = async () => {
    if (outcome === "") { setErr("Оберіть результат"); return; }
    if (needNote && !note.trim()) { setErr("Нотатка обов'язкова для цього результату"); return; }
    if (isPromise && !/^\d{4}-\d{2}-\d{2}$/.test(pp.promisedPaymentDate)) {
      setErr("Вкажіть дату обіцянки оплати"); return;
    }
    const payload: Client360QuickLogPayload = {
      holdingId,
      outcome,
      outcomeNote: note.trim() || undefined,
      contactId: contactId || undefined,
      paymentPromise: isPromise ? pp : undefined,
    };
    try {
      await mutation.mutateAsync(payload);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не вдалося зафіксувати");
    }
  };

  const contacts = contactsQ.data?.items ?? [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full sm:w-[440px] overflow-y-auto bg-background shadow-2xl outline-none"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
            <Phone className="h-4 w-4 text-primary" aria-hidden /> Зафіксувати дзвінок
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium">Контакт <span className="font-normal text-muted-foreground">(опціонально)</span></label>
            <select
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">— не вказувати —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.position ? ` · ${c.position}` : ""}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium">
              Результат (outcome) <span className="text-destructive">*</span> <span className="font-normal text-muted-foreground">— для дзвінка</span>
            </label>
            <OutcomePicker subtype="call" value={outcome} onChange={setOutcome} />
            <p className="mt-1.5 text-[11.5px] text-muted-foreground">Нотатка обов'язкова для «Інше».</p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium">
              Нотатка {needNote && <span className="text-destructive">*</span>}
            </label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Напр. Домовились про оплату до 05.06"
              className={cn(needNote && !note.trim() && err && "border-destructive")}
            />
          </div>

          {isPromise && (
            <PaymentPromiseFields value={pp} onChange={setPp} overdueAmount={overdueAmount} />
          )}

          {err && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {err}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 border-t bg-background px-5 py-4">
          <Button type="button" onClick={submit} disabled={!canSubmit} className="h-11 w-full text-sm font-semibold">
            {mutation.isPending ? (<><Loader2 className="h-4 w-4 animate-spin" /> Збереження…</>) : "Зафіксувати"}
          </Button>
        </div>
      </div>
    </div>
  );
}
